(() => {
  const roots = document.querySelectorAll('[data-cr-early-access]');
  if (!roots.length) return;

  const API_PRODUCTS = '/apps/republic-rank/early-access-products';
  const API_CHECKOUT = '/apps/republic-rank/early-access-checkout';
  const TZ = 'Europe/Bratislava';
  const S = window.cardRepublicStrings?.earlyAccess || {};
  const ranks = window.cardRepublicStrings?.common?.ranks || {};
  const locale = window.cardRepublicStrings?.locale || document.documentElement.lang || 'en';
  const isSk = String(locale).toLowerCase().startsWith('sk');
  const fallback = (sk, en) => isSk ? sk : en;
  const fmt = (value, vars = {}) => Object.entries(vars).reduce(
    (out, [key, replacement]) => String(out || '').replaceAll(`[${key}]`, String(replacement)),
    value || '',
  );
  const rankName = (customer) => {
    const level = Number(customer?.level || 1);
    if (level <= 1) return ranks.novice || S.fallbackRank || 'Novice';
    if (level === 2) return ranks.collector || 'Collector';
    if (level === 3) return ranks.strategist || 'Strategist';
    if (level === 4) return ranks.investor || 'Investor';
    return ranks.elite || 'Republic Elite';
  };

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function money(value, currency) {
    const amount = Number.parseFloat(String(value ?? '0'));
    if (!Number.isFinite(amount)) return '—';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${currency || 'EUR'}`;
    }
  }

  function dateTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat(locale, {
      timeZone: TZ,
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  function countdownParts(seconds) {
    let left = Math.max(0, Math.floor(Number(seconds) || 0));
    const days = Math.floor(left / 86400);
    left %= 86400;
    const hours = Math.floor(left / 3600);
    left %= 3600;
    const minutes = Math.floor(left / 60);
    const secs = left % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (days > 0) return `${days} ${S.dayShort || 'd'} ${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }

  function hideElement(node) {
    if (!node) return;
    node.hidden = true;
    node.setAttribute('hidden', '');
    node.style.display = 'none';
  }

  function getPriceLabel(product) {
    const min = product.minPrice;
    const max = product.maxPrice;
    if (!min) return S.priceCheckout || fallback('Cena pri platbe', 'Price at checkout');
    if (max && min !== max) return fmt(S.fromPrice || 'from [price]', { price: money(min, product.currencyCode) });
    return money(min, product.currencyCode);
  }

  function accessCopy(product, customer, loggedIn) {
    if (!loggedIn) {
      return {
        badge: S.loginBadge || 'SIGN IN',
        badgeClass: '',
        countdown: S.loginCountdown || '',
        button: S.loginButton || fallback('Prihlásiť sa', 'Sign in'),
        disabled: false,
        login: true,
      };
    }

    if (customer.level < 3) {
      return {
        badge: S.level3Badge || 'LEVEL 3+',
        badgeClass: 'is-countdown',
        countdown: fmt(S.levelLocked || fallback('Tvoja úroveň [level] zatiaľ Early Access neodomyká. Verejný predaj začne [date].', 'Your Level [level] does not unlock Early Access yet. Public sale starts [date].'), { level: customer.level, date: dateTime(product.publicLaunchAt) }),
        button: S.level3Button || fallback('Odomkne sa na úrovni 3', 'Unlocks at Level 3'),
        disabled: true,
        login: false,
      };
    }

    if (product.access.allowed) {
      return {
        badge: S.unlockedBadge || 'UNLOCKED',
        badgeClass: 'is-active',
        countdown: fmt(S.unlocked || 'Your [rank] status gives you [hours] h early access. Public sale starts [date].', { rank: rankName(customer), hours: product.access.earlyAccessHours, date: dateTime(product.publicLaunchAt) }),
        button: S.buy || fallback('Kúpiť v Early Access →', 'Buy in Early Access →'),
        disabled: false,
        login: false,
      };
    }

    return {
      badge: S.waitingBadge || 'WAITING TO UNLOCK',
      badgeClass: 'is-countdown',
      countdown: fmt(S.unlockAt || 'Your [rank] access unlocks [date]. Remaining: [countdown].', { rank: rankName(customer), date: dateTime(product.access.unlockAt), countdown: `<b data-cr-countdown data-unlock-at="${escapeHtml(product.access.unlockAt)}">${countdownParts(product.access.remainingSeconds)}</b>` }),
      button: fmt(S.unlockIn || 'Unlocks in [countdown]', { countdown: countdownParts(product.access.remainingSeconds) }),
      disabled: true,
      login: false,
    };
  }

  function variantOptions(product) {
    return (product.variants || []).map((variant) => {
      const unavailable = !variant.availableForSale;
      const title = variant.title === 'Default Title' ? (S.defaultVariant || 'Default variant') : variant.title;
      const price = money(variant.price, product.currencyCode);
      return `<option value="${escapeHtml(variant.numericId)}" ${String(variant.numericId) === String(product.defaultVariantId) ? 'selected' : ''} ${unavailable ? 'disabled' : ''}>${escapeHtml(title)} · ${escapeHtml(price)}${unavailable ? ` · ${escapeHtml(S.unavailable || 'unavailable')}` : ''}</option>`;
    }).join('');
  }

  function productCard(product, customer, loggedIn, loginUrl) {
    const access = accessCopy(product, customer, loggedIn);
    const image = product.image?.url
      ? `<img src="${escapeHtml(product.image.url)}" alt="${escapeHtml(product.image.altText || product.title)}" loading="lazy">`
      : '<div aria-hidden="true"></div>';
    const variants = product.variants || [];
    const hasVariants = variants.length > 1 || (variants[0] && variants[0].title !== 'Default Title');
    const formControls = access.login
      ? `<a class="cr-ea__cta" href="${escapeHtml(loginUrl)}">${escapeHtml(S.loginCta || fallback('Prihlásiť sa →', 'Sign in →'))}</a>`
      : `
        ${hasVariants ? `<select class="cr-ea__select" aria-label="${escapeHtml(S.variantAria || 'Select variant')}" data-cr-variant>${variantOptions(product)}</select>` : `<input type="hidden" data-cr-variant value="${escapeHtml(product.defaultVariantId || '')}">`}
        <input class="cr-ea__qty" type="number" min="1" max="10" step="1" value="1" aria-label="${escapeHtml(S.quantityAria || fallback('Množstvo', 'Quantity'))}" data-cr-quantity ${access.disabled ? 'disabled' : ''}>
        <button class="cr-ea__cta ${access.disabled ? 'is-locked' : ''}" type="button" data-cr-checkout ${access.disabled ? 'disabled' : ''}>${escapeHtml(access.button)}</button>
      `;

    return `
      <article class="cr-ea__card" data-cr-product data-product-id="${escapeHtml(product.numericId)}">
        <div class="cr-ea__media">${image}</div>
        <div class="cr-ea__content">
          <div class="cr-ea__topline">
            <span class="cr-ea__product-kicker">${escapeHtml(S.productKicker || 'REPUBLIC EARLY ACCESS')}</span>
            <span class="cr-ea__badge ${access.badgeClass}">${escapeHtml(access.badge)}</span>
          </div>
          <h3>${escapeHtml(product.title)}</h3>
          <p class="cr-ea__price">${escapeHtml(getPriceLabel(product))}</p>
          <div class="cr-ea__meta">
            <div class="cr-ea__meta-item"><span>${escapeHtml(S.publicSale || 'Public sale')}</span><strong>${escapeHtml(dateTime(product.publicLaunchAt))}</strong></div>
            <div class="cr-ea__meta-item"><span>${escapeHtml(S.yourEarlyAccess || fallback('Tvoj skorší prístup', 'Your early access'))}</span><strong>${loggedIn && customer.level >= 3 ? escapeHtml(fmt(S.hours || '[hours] hours', { hours: product.access.earlyAccessHours })) : escapeHtml(S.level3 || fallback('Úroveň 3+', 'Level 3+'))}</strong></div>
          </div>
          <p class="cr-ea__countdown">${access.countdown}</p>
          <div class="cr-ea__form">${formControls}</div>
          <p class="cr-ea__error" data-cr-error hidden></p>
          <p class="cr-ea__footnote">${escapeHtml(S.footnote || '')}</p>
        </div>
      </article>`;
  }

  function startCountdowns(root) {
    const update = () => {
      root.querySelectorAll('[data-cr-countdown]').forEach((node) => {
        const unlockAt = new Date(node.dataset.unlockAt || '');
        if (Number.isNaN(unlockAt.getTime())) return;
        const seconds = Math.ceil(Math.max(0, unlockAt.getTime() - Date.now()) / 1000);
        node.textContent = countdownParts(seconds);
        if (seconds <= 0) window.location.reload();
      });
    };
    update();
    return window.setInterval(update, 1000);
  }

  async function checkout(root, card, button) {
    const productId = card.dataset.productId;
    const variantControl = card.querySelector('[data-cr-variant]');
    const quantityControl = card.querySelector('[data-cr-quantity]');
    const errorNode = card.querySelector('[data-cr-error]');
    const variantId = variantControl?.value || '';
    const quantity = Math.max(1, Math.min(10, Number.parseInt(quantityControl?.value || '1', 10) || 1));

    errorNode.hidden = true;
    button.classList.add('is-loading');
    button.disabled = true;
    const original = button.textContent;
    button.textContent = S.preparingCheckout || 'Preparing checkout…';

    try {
      const response = await fetch(API_CHECKOUT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, variantId, quantity }),
      });
      const body = await response.json();
      if (!response.ok || !body.success || !body.checkoutUrl) {
        throw new Error(S.checkoutError || 'Early Access checkout could not be created.');
      }
      window.location.assign(body.checkoutUrl);
    } catch (error) {
      errorNode.textContent = error instanceof Error ? error.message : (S.checkoutError || 'Early Access checkout could not be created.');
      errorNode.hidden = false;
      button.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = original;
    }
  }

  async function init(root) {
    const loading = root.querySelector('[data-cr-ea-loading]');
    const grid = root.querySelector('[data-cr-ea-grid]');
    const notice = root.querySelector('[data-cr-ea-notice]');
    const summary = root.querySelector('[data-cr-ea-summary]');
    const member = root.querySelector('[data-cr-ea-member]');
    const memberTitle = root.querySelector('[data-cr-ea-member-title]');
    const memberCopy = root.querySelector('[data-cr-ea-member-copy]');
    const loginUrl = root.dataset.loginUrl || '/account/login';

    try {
      const response = await fetch(API_PRODUCTS, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const body = await response.json();
      if (!response.ok || !body.success) throw new Error(S.loadError || 'Early Access could not be loaded.');

      hideElement(loading);
      member.classList.remove('is-loading');
      const customer = body.customer || { level: 1, rankName: S.fallbackRank || 'Novice', earlyAccessHours: 0 };

      if (!body.loggedIn) {
        member.classList.add('is-locked');
        memberTitle.textContent = S.loginTitle || fallback('Prihlás sa do Card Republic', 'Sign in to Card Republic');
        memberCopy.textContent = S.loginCopy || '';
      } else if (customer.eligibleForEarlyAccess) {
        member.classList.add('is-active');
        memberTitle.textContent = `Level ${customer.level} · ${rankName(customer)}`;
        memberCopy.textContent = fmt(S.memberCopy || '[hours] h early access to selected products.', { hours: customer.earlyAccessHours });
      } else {
        member.classList.add('is-locked');
        memberTitle.textContent = `Level ${customer.level} · ${rankName(customer)}`;
        memberCopy.textContent = S.lockedMemberCopy || fallback('Early Access sa automaticky odomkne na úrovni 3.', 'Early Access unlocks automatically at Level 3.');
      }

      const products = Array.isArray(body.products) ? body.products : [];
      summary.textContent = products.length === 1 ? (S.summaryOne || '1 product is waiting for public launch') : fmt(S.summaryOther || '[count] products are waiting for public launch', { count: products.length });

      if (!products.length) {
        notice.hidden = false;
        notice.innerHTML = `<strong>${escapeHtml(S.noProductsTitle || fallback('Momentálne tu nie je žiadny aktívny Early Access produkt.', 'There are currently no active Early Access products.'))}</strong><p>${escapeHtml(S.noProductsCopy || '')}</p>`;
        return;
      }

      grid.innerHTML = products.map((product) => productCard(product, customer, body.loggedIn, loginUrl)).join('');
      grid.hidden = false;

      grid.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cr-checkout]');
        if (!button || button.disabled) return;
        const card = button.closest('[data-cr-product]');
        if (!card) return;
        checkout(root, card, button);
      });

      startCountdowns(root);
    } catch (error) {
      hideElement(loading);
      notice.hidden = false;
      notice.innerHTML = `<strong>${escapeHtml(S.loadFailedTitle || fallback('Early Access sa nepodarilo načítať', 'Early Access could not be loaded'))}</strong><p>${escapeHtml(S.loadFailedCopy || '')}</p>`;
      summary.textContent = S.tempUnavailable || 'Temporarily unavailable.';
      member.classList.remove('is-loading');
      member.classList.add('is-locked');
      memberTitle.textContent = S.statusFailed || 'Status could not be verified';
      memberCopy.textContent = S.statusFailedCopy || '';
    }
  }

  roots.forEach(init);
})();
